/**
 * Shared activity feed component — renders `account.getActivity` results.
 * Used by both portals' Activity tab on the Accounts detail page.
 */

import React from 'react';
import {trpc} from '@common-web/lib/trpc';
import {useAccountsViewConfig} from '@common-web/hooks/useAccountsViewConfig';
import type {AccountsSourceEntity} from '@api/generated/client';
import {describeAccountActivity} from './describeAccountActivity';

interface AccountActivityFeedProps {
  accountsId: string;
  sourceEntityType?: AccountsSourceEntity;
}

function formatAction(action: string): string {
  return action
    .toLowerCase()
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function AccountActivityFeed({
  accountsId,
  sourceEntityType,
}: AccountActivityFeedProps): React.ReactElement {
  const config = useAccountsViewConfig();
  const activity = trpc.account.getActivity.useQuery({
    accountsId,
    sourceEntityType,
  });

  if (activity.isLoading) {
    return <div className='text-sm text-muted-foreground py-4'>Loading activity…</div>;
  }
  if (activity.error) {
    return (
      <div className='text-sm text-red-600 py-4'>
        Failed to load activity: {activity.error.message}
      </div>
    );
  }
  if (!activity.data || activity.data.length === 0) {
    return <div className='text-sm text-muted-foreground py-8 text-center'>No activity yet.</div>;
  }

  return (
    <ol className='space-y-2'>
      {activity.data.map((event) => {
        const detail = describeAccountActivity(event, config);
        return (
          <li key={event.id} className='border-l-2 border-muted pl-3 py-1 text-sm'>
            <div className='font-medium'>{formatAction(event.action)}</div>
            {detail && <div className='mt-0.5'>{detail}</div>}
            <div className='text-xs text-muted-foreground mt-0.5'>
              {event.actorUser?.name ?? 'System'} · {new Date(event.createdAt).toLocaleString()}
              {event.sourceEntityType ? ` · ${event.sourceEntityType}` : ''}
            </div>
          </li>
        );
      })}
    </ol>
  );
}
